import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Controller, useFormContext, useWatch } from 'react-hook-form';
import cn from 'classnames';

import {
  Button,
  Icon,
  Input,
  Select,
  Divider,
  MultiSelectDropdown,
} from '../../components';

import { mapServiceTypes, serviceTypes } from '../../utils';

import { mapRewardType, RewardTypes } from './loyaltyProgram.data';

import styles from './loyaltyProgram.module.scss';

const serviceOptions = [
  { name: 'Pasirinkti', value: '' },
  ...serviceTypes.map(type => ({
    name: mapServiceTypes(type),
    value: String(type),
  })),
];

const paymentMethodOptions = [
  { name: 'Piniginė', value: 101 },
  { name: 'Paslauga', value: 102 },
];

const rewardTypeOptions = Object.values(RewardTypes).map(type => ({
  name: mapRewardType(type),
  value: type,
}));

const GoalsGroupForm = ({
  index,
  control,
  onRemove,
  disableDelete,
  memberships: { memberships = [], loading: membershipsLoading = false } = {},
  loading,
}) => {
  const { setValue } = useFormContext();

  const [isFocused, setIsFocused] = useState(false);
  const formatter = val => `${val} %`;
  const parser = val => String(val).replace(' %', '');

  const goal = useWatch({
    control,
    name: `goals.${index}`,
  });
  const { type, reward_type } = goal;

  const membershipOptions = [
    { name: 'Pasirinkti', value: '' },
    ...memberships.map(m => ({
      name: m.title_lt,
      value: m.id,
    })),
  ];

  useEffect(() => {
    if (type !== 102) {
      setValue(`goals.${index}.service_type`, '');
    }
  }, [type]);

  useEffect(() => {
    if (reward_type !== RewardTypes.DISCOUNT) {
      setValue(`goals.${index}.discount_percent`, 0);
      setValue(`goals.${index}.reward_service_types`, []);
    }

    if (reward_type !== RewardTypes.VIRTUAL) {
      setValue(`goals.${index}.wallet_reward`, 0);
    }

    if (reward_type !== RewardTypes.MEMBERSHIP) {
      setValue(`goals.${index}.membership_id`, '');
      setValue(`goals.${index}.discount_valid_days`, 0);
    }
  }, [reward_type]);

  return (
    <div className={styles.goalContainer}>
      <span className={styles.goalIndex}>{index + 1}</span>
      <div className={styles.goalInputsContainer}>
        <Divider titlePlacement="left" className={styles.goalSectionDivider}>
          Sąlygos
        </Divider>
        <div className={styles.inputsInner}>
          <div
            className={cn(
              styles.goalInputs,
              type !== 102 && styles.goalInputsTwoFields
            )}
          >
            <Controller
              name={`goals.${index}.amount_goal`}
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Input
                  name={`goals.${index}.amount_goal`}
                  label="Sumos tikslas, EUR"
                  placeholder="10"
                  type="text"
                  value={value ?? ''}
                  onChange={e =>
                    onChange(e.target.value.replace(/\D/g, '') || 0)
                  }
                  disabled={loading}
                  helperText={error?.message}
                />
              )}
            />
            <Controller
              name={`goals.${index}.type`}
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <div className={styles.field}>
                  <label className={styles.label}>Tipas</label>
                  <Select
                    name={`goals.${index}.type`}
                    options={paymentMethodOptions}
                    selectedValue={value}
                    onChange={event => onChange(Number(event.target.value))}
                  />
                  {error && (
                    <span className={styles.errorMessage}>{error.message}</span>
                  )}
                </div>
              )}
            />
            {type === 102 && (
              <Controller
                name={`goals.${index}.service_type`}
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <div className={styles.field}>
                    <label className={styles.label}>Paslauga</label>
                    <Select
                      name={`goals.${index}.service_type`}
                      options={serviceOptions}
                      selectedValue={
                        value === undefined || value === null ? '' : value
                      }
                      onChange={event =>
                        onChange(
                          event.target.value === ''
                            ? ''
                            : Number(event.target.value)
                        )
                      }
                    />
                    {error && (
                      <span className={styles.errorMessage}>
                        {error.message}
                      </span>
                    )}
                  </div>
                )}
              />
            )}
          </div>
          <Button
            color="dim"
            aria-label="delete-goal"
            disabled={disableDelete}
            type="button"
            classes={{ button: styles.deleteButton }}
            onClick={onRemove}
          >
            <Icon name="delete" classes={{ icon: styles.deleteIcon }} />
          </Button>
        </div>
        <Divider titlePlacement="left" className={styles.goalSectionDivider}>
          Prizas
        </Divider>
        <div className={styles.inputsInner}>
          <div className={styles.goalInputs}>
            <Controller
              name={`goals.${index}.reward_type`}
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <div className={styles.field}>
                  <label className={styles.label}>Tipas</label>
                  <Select
                    name={`goals.${index}.reward_type`}
                    options={rewardTypeOptions}
                    selectedValue={value}
                    onChange={event => onChange(Number(event.target.value))}
                    labelContainerClassName={
                      styles.selectLabelContainerClassName
                    }
                  />
                  {error && (
                    <span className={styles.errorMessage}>{error.message}</span>
                  )}
                </div>
              )}
            />
            {reward_type === RewardTypes.DISCOUNT && (
              <>
                <Controller
                  name={`goals.${index}.reward_service_types`}
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <div className={styles.field}>
                      <label className={styles.label}>Paslauga</label>
                      <MultiSelectDropdown
                        array={serviceOptions}
                        valueType="number"
                        state={value}
                        setState={onChange}
                        placeholder="Pasirinkti"
                        allText="Visi"
                      />
                      {error && (
                        <span className={styles.errorMessage}>
                          {error.message}
                        </span>
                      )}
                    </div>
                  )}
                />
                <Controller
                  name={`goals.${index}.discount_percent`}
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <Input
                      name={`goals.${index}.discount_percent`}
                      label="Nuolaida, %"
                      placeholder="5"
                      type="text"
                      value={
                        value === '' || value === undefined
                          ? ''
                          : isFocused
                          ? value
                          : formatter(value)
                      }
                      onChange={e => {
                        const parsed = parser(e.target.value).replace(
                          /\D/g,
                          ''
                        );
                        let number = Number(parsed);
                        if (number > 100) number = 100;
                        onChange(number);
                      }}
                      onFocus={() => setIsFocused(true)}
                      onBlur={() => setIsFocused(false)}
                      disabled={loading}
                      helperText={error?.message}
                    />
                  )}
                />
              </>
            )}
            {reward_type === RewardTypes.VIRTUAL && (
              <Controller
                name={`goals.${index}.wallet_reward`}
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <Input
                    name={`goals.${index}.wallet_reward`}
                    label="Suma"
                    placeholder="10"
                    type="number"
                    value={value ?? ''}
                    onChange={e =>
                      onChange(
                        e.target.value === '' ? '' : Number(e.target.value)
                      )
                    }
                    disabled={loading}
                    helperText={error?.message}
                  />
                )}
              />
            )}
            {reward_type === RewardTypes.MEMBERSHIP && (
              <>
                <Controller
                  name={`goals.${index}.membership_id`}
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <div className={styles.field}>
                      <label className={styles.label}>Plano pasirinkimas</label>
                      <Select
                        name={`goals.${index}.membership_id`}
                        options={membershipOptions}
                        selectedValue={
                          value === undefined || value === null ? '' : value
                        }
                        disabled={loading || membershipsLoading}
                        onChange={event =>
                          onChange(
                            event.target.value === ''
                              ? ''
                              : Number(event.target.value)
                          )
                        }
                        labelContainerClassName={
                          styles.selectLabelContainerClassName
                        }
                      />
                      {error && (
                        <span className={styles.errorMessage}>
                          {error.message}
                        </span>
                      )}
                    </div>
                  )}
                />
                <Controller
                  name={`goals.${index}.discount_valid_days`}
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <Input
                      name={`goals.${index}.discount_valid_days`}
                      label="Nuo aktyvavimo galioja dienų"
                      type="number"
                      value={value ?? ''}
                      onChange={e =>
                        onChange(
                          e.target.value === '' ? '' : Number(e.target.value)
                        )
                      }
                      disabled={loading}
                      helperText={error?.message}
                    />
                  )}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

GoalsGroupForm.propTypes = {
  index: PropTypes.number.isRequired,
  control: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
  disableDelete: PropTypes.bool.isRequired,
  memberships: PropTypes.shape({
    memberships: PropTypes.array,
    loading: PropTypes.bool,
  }),
  loading: PropTypes.bool,
};

export default GoalsGroupForm;
